import {
  AppWindow,
  Archive,
  AudioLines,
  Bell,
  Blocks,
  BookOpen,
  Bot,
  Bug,
  Building2,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  Circle,
  CirclePlay,
  Clock,
  Code,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  Folder,
  Gauge,
  Globe,
  HardDrive,
  History,
  Image,
  Inbox,
  InfinityIcon,
  KeyRound,
  LayoutTemplate,
  Link2,
  List,
  ListChecks,
  ListTodo,
  Mail,
  Mailbox,
  Mails,
  MessageSquare,
  Music2,
  Orbit,
  Palette,
  PenSquare,
  Pin,
  Play,
  Plug,
  Plus,
  Power,
  Presentation,
  Redo2,
  RefreshCw,
  Rocket,
  ScanFace,
  ScrollText,
  Search,
  Send,
  Settings,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Terminal,
  Trash2,
  Trophy,
  Undo2,
  UserRound,
  Users,
  UsersRound,
  Video,
  Workflow,
} from 'lucide-react';

import { createElement, forwardRef } from 'react';

const customIcons = new Map<string, typeof AppWindow>();
const icons = {
  // The sections and actions of the Crowlingo applications shown embedded (the embed bridge).
  'building-2': Building2,
  'calendar-clock': CalendarClock,
  'calendar-days': CalendarDays,
  clock: Clock,
  folder: Folder,
  'hard-drive': HardDrive,
  inbox: Inbox,
  mailbox: Mailbox,
  mails: Mails,
  'pen-square': PenSquare,
  plus: Plus,
  search: Search,
  send: Send,
  settings: Settings,
  star: Star,
  'trash-2': Trash2,
  'users-round': UsersRound,
  // CrowBox's agenda, CrowRoom's and Crowpus' sections.
  'book-open': BookOpen,
  eye: Eye,
  'eye-off': EyeOff,
  gauge: Gauge,
  'key-round': KeyRound,
  'layout-template': LayoutTemplate,
  'link-2': Link2,
  'scroll-text': ScrollText,
  'shield-check': ShieldCheck,
  trophy: Trophy,
  archive: Archive,
  'audio-lines': AudioLines,
  bell: Bell,
  circle: Circle,
  'circle-play': CirclePlay,
  copy: Copy,
  download: Download,
  history: History,
  plug: Plug,
  power: Power,
  'refresh-cw': RefreshCw,
  'redo-2': Redo2,
  rocket: Rocket,
  'settings-2': Settings2,
  'undo-2': Undo2,
  bot: Bot,
  bug: Bug,
  'chart-column': ChartColumn,
  palette: Palette,
  infinity: InfinityIcon,
  'message-square': MessageSquare,
  music: Music2,
  orbit: Orbit,
  'app-window': AppWindow,
  app: AppWindow,
  blocks: Blocks,
  code: Code,
  'file-text': FileText,
  globe: Globe,
  image: Image,
  list: List,
  'list-checks': ListChecks,
  'sliders-horizontal': SlidersHorizontal,
  'list-todo': ListTodo,
  mail: Mail,
  pin: Pin,
  play: Play,
  presentation: Presentation,
  'scan-face': ScanFace,
  sparkles: Sparkles,
  terminal: Terminal,
  'user-round': UserRound,
  users: Users,
  video: Video,
  workflow: Workflow,
};
export function pluginIcon(name: string): typeof AppWindow {
  if (/^(https:\/\/|data:image\/(svg\+xml|png|jpeg|webp|gif);base64,)/.test(name)) {
    const existing = customIcons.get(name);
    if (existing) return existing;
    // biome-ignore lint/correctness/noComponentHookFactories: made once per image URL and kept in customIcons, so each icon keeps one component identity across renders.
    const Icon: typeof AppWindow = forwardRef((props, ref) =>
      createElement(
        'svg',
        {
          ...props,
          ref,
          viewBox: '0 0 24 24',
          fill: 'none',
        },
        createElement('image', { href: name, width: 24, height: 24 }),
      ),
    );
    Icon.displayName = 'PluginIcon';
    const oldest = customIcons.keys().next().value;
    if (customIcons.size >= 128 && oldest) customIcons.delete(oldest);
    customIcons.set(name, Icon);
    return Icon;
  }
  return icons[name as keyof typeof icons] ?? AppWindow;
}
